// Plain-language guidance for a first-time bank/ACH setup. Shared by all three
// Payment Setup tabs (buyer, seller, broker) so the "where do I find these
// numbers?" help reads identically everywhere. Pure presentational — no fetch,
// no state.

import { useState } from "react";
import { Button, Card, CardContent, GlossaryHint } from "@/design-system/components/ui";

interface BankDetailsHelpProps {
  /** Tunes the reassurance copy: a borrower's bank details are held by the
   *  licensed servicer on their loan; a payee's by our payout processor. */
  context: "borrower" | "payee";
}

export function BankDetailsHelp({ context }: BankDetailsHelpProps) {
  const [showFaq, setShowFaq] = useState(false);

  const reassurance =
    context === "borrower"
      ? "Your bank details are handled securely by our payment processor and the licensed servicer assigned to your loan. Phoenix never stores your full account number."
      : "Your bank details are handled securely by our payout processor. Phoenix never stores your full account number.";

  return (
    <Card data-testid="bank-details-help">
      <CardContent className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
          What you&apos;ll need
        </h3>

        <div className="space-y-space-2 text-small text-onyx">
          <p className="m-0">
            Have a paper check handy if you have one — the two number groups
            along the bottom are all you need:
          </p>
          <ul className="list-disc space-y-space-1 pl-space-5 m-0">
            <li>
              Your{" "}
              <GlossaryHint term="routing number">routing number</GlossaryHint> —
              the 9-digit group on the <span className="font-semibold">left</span>,
              which identifies your bank.
            </li>
            <li>
              Your{" "}
              <GlossaryHint term="account number">account number</GlossaryHint> —
              the group{" "}
              <span className="font-semibold">to the right</span> of the routing
              number, which identifies your specific account.
            </li>
            <li>
              Your{" "}
              <GlossaryHint term="account type">account type</GlossaryHint> —
              whether it&apos;s a <span className="font-semibold">checking</span>{" "}
              or a <span className="font-semibold">savings</span> account. Your
              bank statement or checkbook will say which.
            </li>
          </ul>
          <p className="m-0 text-caption text-gray-mid">
            No paper check? You can find all three in your bank&apos;s app or
            website, usually under &ldquo;account details.&rdquo;
          </p>
        </div>

        <p
          className="rounded-lg border border-border-light bg-white-warm p-space-3 text-caption text-gray-mid m-0"
          data-testid="bank-details-reassurance"
        >
          {reassurance}
        </p>

        <div>
          <Button
            variant="text"
            size="sm"
            onClick={() => setShowFaq((v) => !v)}
            aria-expanded={showFaq}
            data-testid="bank-details-faq-toggle"
          >
            {showFaq ? "Hide common questions" : "Common questions"}
          </Button>
          {showFaq && (
            <dl className="mt-space-2 space-y-space-3 text-small" data-testid="bank-details-faq">
              <FaqItem
                q="Is it safe to enter my bank details here?"
                a={reassurance}
              />
              <FaqItem
                q="Will connecting my bank charge me anything?"
                a="No. Connecting your bank account doesn't move any money. It only saves the account so future payments can be handled without re-entering it."
              />
              <FaqItem
                q="Can I change the account later?"
                a="Yes. You can come back to this page any time to update the bank account on file."
              />
              <FaqItem
                q="What if my bank shows a longer account number?"
                a="Enter the full number exactly as your bank shows it. If a leading zero or two appears, include them — don't drop any digits."
              />
            </dl>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div>
      <dt className="font-semibold text-onyx">{q}</dt>
      <dd className="m-0 mt-[2px] text-gray-mid">{a}</dd>
    </div>
  );
}
